import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    correo: '',
    comuna: '',
    mensaje: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const message =
      `Hola! Te contacto desde tu sitio web.\n\n` +
      `*Nombre:* ${formData.nombre}\n` +
      `*Teléfono:* ${formData.telefono}\n` +
      `*Correo:* ${formData.correo}\n` +
      `*Comuna:* ${formData.comuna}\n` +
      `*Mensaje:* ${formData.mensaje}`;

    const encoded = encodeURIComponent(message);
    window.open(`https://wa.me/56944540956?text=${encoded}`, '_blank');

    setSubmitStatus('success');
    setIsSubmitting(false);
    setFormData({ nombre: '', telefono: '', correo: '', comuna: '', mensaje: '' });
  };

  return (
    <section id="contacto" className="py-24 lg:py-32 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block px-4 py-2 bg-green-50 rounded-full mb-6">
            <span className="text-sm font-medium text-green-600">Contacto</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Conversemos sobre tu salud previsional
          </h2>
          <p className="text-lg text-gray-700">
            Si necesitas orientación personalizada, escríbeme y conversemos
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Info */}
          <div className="space-y-8">
            <div className="bg-white rounded-2xl p-8 border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Información de contacto
              </h3>
              
              <div className="space-y-6">
                {/* WhatsApp */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-whatsapp-line text-2xl text-green-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">WhatsApp</h4>
                    <p className="text-gray-600 mb-2">Respuesta rápida y directa</p>
                    <button
                      onClick={() => window.open('https://wa.me/56944540956', '_blank')}
                      className="text-green-600 hover:text-green-700 font-medium whitespace-nowrap cursor-pointer"
                    >
                      +56 9 4454 0956
                    </button>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-2xl text-gray-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Correo electrónico</h4>
                    <p className="text-gray-600 mb-2">Escríbeme cuando prefieras</p>
                    <a
                      href="mailto:susana@asesora-salud.cl"
                      className="text-gray-700 hover:text-gray-900 font-medium whitespace-nowrap"
                    >
                      susana@asesora-salud.cl
                    </a>
                  </div>
                </div>

                {/* Location */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-2xl text-gray-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Ubicación</h4>
                    <p className="text-gray-600">Santiago, Chile</p>
                  </div>
                </div>

                {/* Schedule */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-2xl text-gray-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Horario de atención</h4>
                    <p className="text-gray-600">Lunes a Viernes: 9:00 - 19:00</p>
                    <p className="text-gray-600">Sábados: 10:00 - 14:00</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Trust Badge */}
            <div className="bg-gradient-to-br from-green-50 to-white rounded-2xl p-8 border border-green-100">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-2xl text-green-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Tu privacidad es importante</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Tus datos personales son tratados con total confidencialidad y solo se utilizan para brindarte la mejor orientación posible.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl p-8 border border-gray-100">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                <i className="ri-whatsapp-line text-xl text-green-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">
                Solicita orientación
              </h3>
            </div>
            <p className="text-sm text-gray-500 mb-6">
              Completa el formulario y se abrirá WhatsApp con tu mensaje listo para enviar.
            </p>

            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
              {/* Nombre */}
              <div>
                <label htmlFor="nombre" className="block text-sm font-medium text-gray-900 mb-2">
                  Nombre completo *
                </label>
                <input
                  type="text"
                  id="nombre"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent text-sm"
                  placeholder="Tu nombre"
                />
              </div>

              {/* Teléfono */}
              <div>
                <label htmlFor="telefono" className="block text-sm font-medium text-gray-900 mb-2">
                  Teléfono *
                </label>
                <input
                  type="tel"
                  id="telefono"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent text-sm"
                  placeholder="+56 9 1234 5678"
                />
              </div>

              {/* Correo */}
              <div>
                <label htmlFor="correo" className="block text-sm font-medium text-gray-900 mb-2">
                  Correo electrónico *
                </label>
                <input
                  type="email"
                  id="correo"
                  name="email"
                  value={formData.correo}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent text-sm"
                  placeholder="tu@email.com"
                />
              </div>

              {/* Comuna */}
              <div>
                <label htmlFor="comuna" className="block text-sm font-medium text-gray-900 mb-2">
                  Comuna *
                </label>
                <input
                  type="text"
                  id="comuna"
                  name="comuna"
                  value={formData.comuna}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent text-sm"
                  placeholder="Tu comuna"
                />
              </div>

              {/* Mensaje */}
              <div>
                <label htmlFor="mensaje" className="block text-sm font-medium text-gray-900 mb-2">
                  Mensaje *
                </label>
                <textarea
                  id="mensaje"
                  name="mensaje"
                  value={formData.mensaje}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent text-sm resize-none"
                  placeholder="Cuéntame qué necesitas y cómo puedo ayudarte..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{formData.mensaje.length}/500 caracteres</p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center space-x-2 cursor-pointer"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                <span>{isSubmitting ? 'Abriendo WhatsApp...' : 'Enviar por WhatsApp'}</span>
              </button>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                  <p className="text-sm text-green-800 flex items-center">
                    <i className="ri-checkbox-circle-line text-lg mr-2"></i>
                    WhatsApp abierto con tu mensaje listo. ¡Solo presiona enviar!
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}